'use client'

import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useAuth } from '@/context/AuthContext'
import { getUserProfile } from '@/lib/userProfile'
import { AIRPORT_DB, getAirportLabel, getAirportTooltip, getAirportCity } from '@/lib/airports'
import { useCurrency } from '@/lib/currency'

function dealMatchesWindows(deal, windows) {
  if (!windows.length || !deal.departureDate) return false
  return windows.some((w) => deal.departureDate >= w.start && deal.departureDate <= w.end)
}

function flagEmoji(countryCode) {
  return [...countryCode.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join('')
}

const REGION_ORDER = ['europe', 'asia', 'americas', 'middle_east', 'africa', 'oceania']
const REGION_LABELS = { europe: 'Europe', asia: 'Asia', americas: 'Americas', africa: 'Africa', middle_east: 'Middle East', oceania: 'Oceania' }
const REGION_ICONS  = { europe: '🏛️', asia: '🏯', americas: '🗽', africa: '🌍', middle_east: '🕌', oceania: '🌊' }
const REGION_STYLES = {
  europe:      { bg: '#EFF6FF', text: '#1E40AF', count: '#3B82F6' },
  asia:        { bg: '#F0FDF4', text: '#14532D', count: '#16A34A' },
  americas:    { bg: '#F5F3FF', text: '#3B0764', count: '#7C3AED' },
  africa:      { bg: '#FFF7ED', text: '#7C2D12', count: '#EA580C' },
  middle_east: { bg: '#FFFBEB', text: '#78350F', count: '#D97706' },
  oceania:     { bg: '#ECFDF5', text: '#064E3B', count: '#059669' },
}

function hoursAgo(iso) {
  if (!iso) return null
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000)
  if (h < 1) return 'just now'
  if (h < 48) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const SORT_OPTIONS = [
  { key: 'freq', label: 'Most popular' },
  { key: 'az', label: 'A–Z' },
  { key: 'most_deals', label: 'Most deals' },
  { key: 'cheapest', label: 'Lowest price' },
  { key: 'best_discount', label: 'Most below avg' },
  { key: 'recent', label: 'Recently detected' },
]

export default function DestinationsGrid({ destinations, deals, dealFreq, cheapestMonths }) {
  const { currentUser } = useAuth()
  const { formatDealPrice } = useCurrency()

  const [hasDealsOnly, setHasDealsOnly] = useState(false)
  const [fromMyAirports, setFromMyAirports] = useState(false)
  const [homeAirports, setHomeAirports] = useState([])
  const [travelWindows, setTravelWindows] = useState([])
  const [matchMyDates, setMatchMyDates]   = useState(false)
  const [sort, setSort] = useState('freq')
  const searchParams = useSearchParams()
  const [activeRegion, setActiveRegion] = useState(() => searchParams.get('region') ?? 'all')

  // Advanced filters
  const [filterOpen, setFilterOpen] = useState(false)
  const [controlsOpen, setControlsOpen] = useState(false)
  const [isPaid, setIsPaid] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [selectedAirports, setSelectedAirports] = useState([])

  // Load home airports + travel windows + tier when user is authenticated
  useEffect(() => {
    if (!currentUser?.uid) {
      setHomeAirports([])
      setFromMyAirports(false)
      setTravelWindows([])
      setIsPaid(false)
      return
    }
    getUserProfile(currentUser.uid)
      .then((p) => {
        if (!p) return
        const airports = [
          ...(Array.isArray(p.homeAirports) ? p.homeAirports : []),
          ...(p.homeAirport ? [p.homeAirport] : []),
        ].filter((v, i, a) => a.indexOf(v) === i)
        setHomeAirports(airports)
        setTravelWindows(Array.isArray(p.travelWindows) ? p.travelWindows : [])
        setIsPaid(p.tier === 'plus' || p.tier === 'pro')
      })
      .catch(() => {})
  }, [currentUser?.uid])

  // All deals indexed by destination code
  const allDealsByDest = useMemo(() => {
    const map = {}
    for (const deal of deals) {
      if (!map[deal.destination]) map[deal.destination] = []
      map[deal.destination].push(deal)
    }
    return map
  }, [deals])

  // Deals filtered to home airports only
  const myDealsByDest = useMemo(() => {
    if (!homeAirports.length) return allDealsByDest
    const map = {}
    for (const deal of deals) {
      if (!homeAirports.includes(deal.origin)) continue
      if (!map[deal.destination]) map[deal.destination] = []
      map[deal.destination].push(deal)
    }
    return map
  }, [deals, homeAirports, allDealsByDest])

  const activeDealsByDest = fromMyAirports && homeAirports.length ? myDealsByDest : allDealsByDest

  // Group destinations by city_name; primary = most active deals, alpha tiebreaker
  const groupedDestinations = useMemo(() => {
    const groups = new Map()
    for (const dest of destinations) {
      const key = dest.city_name
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(dest)
    }
    return [...groups.values()].map((dests) => {
      if (dests.length === 1) return { primary: dests[0], airports: dests }
      const sorted = [...dests].sort((a, b) => {
        const diff = (allDealsByDest[b.code]?.length ?? 0) - (allDealsByDest[a.code]?.length ?? 0)
        return diff !== 0 ? diff : a.code.localeCompare(b.code)
      })
      return { primary: sorted[0], airports: sorted }
    })
  }, [destinations, allDealsByDest])

  // Available regions from data
  const regions = useMemo(() => {
    const found = new Set(destinations.map((d) => d.region).filter(Boolean))
    return REGION_ORDER.filter((r) => found.has(r))
  }, [destinations])

  const regionCounts = useMemo(() => {
    const destRegion = {}
    for (const d of destinations) {
      if (d.code && d.region) destRegion[d.code] = d.region
    }
    const sets = { all: new Set() }
    for (const deal of deals) {
      sets.all.add(deal.destination)
      const region = destRegion[deal.destination]
      if (region) {
        if (!sets[region]) sets[region] = new Set()
        sets[region].add(deal.destination)
      }
    }
    const counts = {}
    for (const [key, set] of Object.entries(sets)) counts[key] = set.size
    return counts
  }, [deals, destinations])

  // Available departure airports across all deals (for airport chips)
  const availableAirports = useMemo(() => [...new Set(deals.map((d) => d.origin))].sort(), [deals])

  // Count city groups with deals (for badge on toggle)
  const totalWithDeals = useMemo(
    () => groupedDestinations.filter(({ airports }) =>
      airports.some((d) => (activeDealsByDest[d.code]?.length ?? 0) > 0)
    ).length,
    [groupedDestinations, activeDealsByDest]
  )

  // Count city groups with deals matching travel windows
  const matchingDestsCount = useMemo(
    () =>
      travelWindows.length
        ? groupedDestinations.filter(({ airports }) =>
            airports.some((d) =>
              (activeDealsByDest[d.code] ?? []).some((deal) => dealMatchesWindows(deal, travelWindows))
            )
          ).length
        : 0,
    [groupedDestinations, activeDealsByDest, travelWindows]
  )

  // Apply filters + sort on city groups
  const filtered = useMemo(() => {
    let list = groupedDestinations

    // Region filter
    if (activeRegion !== 'all') {
      list = list.filter(({ primary }) => primary.region === activeRegion)
    }

    // Has deals only
    if (hasDealsOnly) {
      list = list.filter(({ airports }) =>
        airports.some((d) => (activeDealsByDest[d.code]?.length ?? 0) > 0)
      )
    }

    // Matches my travel windows
    if (matchMyDates && travelWindows.length) {
      list = list.filter(({ airports }) =>
        airports.some((d) =>
          (activeDealsByDest[d.code] ?? []).some((deal) => dealMatchesWindows(deal, travelWindows))
        )
      )
    }

    // Max price filter — any airport in group must have at least one deal ≤ maxPrice
    if (maxPrice) {
      const mp = Number(maxPrice)
      if (!isNaN(mp) && mp > 0) {
        list = list.filter(({ airports }) =>
          airports.some((d) => (activeDealsByDest[d.code] ?? []).some((deal) => deal.price <= mp))
        )
      }
    }

    // Date range filter — any airport in group must have at least one deal in range
    if (dateFrom || dateTo) {
      list = list.filter(({ airports }) =>
        airports.some((d) =>
          (activeDealsByDest[d.code] ?? []).some((deal) => {
            if (!deal.departureDate) return false
            if (dateFrom && deal.departureDate < dateFrom) return false
            if (dateTo && deal.departureDate > dateTo) return false
            return true
          })
        )
      )
    }

    // Departure airport filter
    if (selectedAirports.length > 0) {
      list = list.filter(({ airports }) =>
        airports.some((d) =>
          (activeDealsByDest[d.code] ?? []).some((deal) => selectedAirports.includes(deal.origin))
        )
      )
    }

    // Sort
    list = [...list]
    if (sort === 'freq') {
      list.sort((a, b) => {
        const fa = a.airports.reduce((s, d) => s + (dealFreq[d.code] ?? 0), 0)
        const fb = b.airports.reduce((s, d) => s + (dealFreq[d.code] ?? 0), 0)
        return fb !== fa ? fb - fa : a.primary.city_name.localeCompare(b.primary.city_name)
      })
    } else if (sort === 'az') {
      list.sort((a, b) => a.primary.city_name.localeCompare(b.primary.city_name))
    } else if (sort === 'most_deals') {
      list.sort((a, b) => {
        const aCount = a.airports.reduce((s, d) => s + (activeDealsByDest[d.code]?.length ?? 0), 0)
        const bCount = b.airports.reduce((s, d) => s + (activeDealsByDest[d.code]?.length ?? 0), 0)
        return bCount - aCount
      })
    } else if (sort === 'cheapest') {
      list.sort((a, b) => {
        const minPrice = (airports) => {
          const prices = airports.flatMap((d) => (activeDealsByDest[d.code] ?? []).map((deal) => deal.price))
          return prices.length ? Math.min(...prices) : Infinity
        }
        return minPrice(a.airports) - minPrice(b.airports)
      })
    } else if (sort === 'best_discount') {
      list.sort((a, b) => {
        const maxDiscount = (airports) => {
          const discounts = airports.flatMap((d) => (activeDealsByDest[d.code] ?? []).map((deal) => deal.discountPct ?? 0))
          return discounts.length ? Math.max(...discounts) : 0
        }
        return maxDiscount(b.airports) - maxDiscount(a.airports)
      })
    } else if (sort === 'recent') {
      list.sort((a, b) => {
        const latest = (airports) => airports.flatMap((d) =>
          (activeDealsByDest[d.code] ?? []).map((deal) => deal.fetched_at ?? '')
        ).reduce((max, t) => t > max ? t : max, '')
        return latest(b.airports).localeCompare(latest(a.airports))
      })
    }

    return list
  }, [groupedDestinations, activeRegion, hasDealsOnly, matchMyDates, travelWindows, activeDealsByDest, dealFreq, sort, maxPrice, dateFrom, dateTo, selectedAirports])



  return (
    <>
      {/* ── Controls ── */}
      <div className="space-y-3 mb-6">
        {/* Region blocks */}
        {regions.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <button
              onClick={() => setActiveRegion('all')}
              className={`rounded-xl p-3 text-left transition-all ${activeRegion === 'all' ? 'ring-2 ring-blue-500 bg-blue-50' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              <div className="text-base mb-1">🌐</div>
              <div className="font-semibold text-xs text-gray-800 leading-tight">All regions</div>
              {regionCounts.all > 0 && <div className="text-xs text-gray-500 tabular-nums mt-0.5">{regionCounts.all}</div>}
            </button>
            {regions.map((r) => {
              const s = REGION_STYLES[r] ?? { bg: '#F3F4F6', text: '#374151', count: '#6B7280' }
              return (
                <button
                  key={r}
                  onClick={() => setActiveRegion((prev) => prev === r ? 'all' : r)}
                  className={`rounded-xl p-3 text-left transition-all ${activeRegion === r ? 'ring-2 ring-blue-500' : 'hover:opacity-80'}`}
                  style={{ backgroundColor: s.bg }}
                >
                  <div className="text-base mb-1">{REGION_ICONS[r] ?? '✈️'}</div>
                  <div className="font-semibold text-xs leading-tight" style={{ color: s.text }}>{REGION_LABELS[r] ?? r}</div>
                  {regionCounts[r] != null && <div className="text-xs tabular-nums mt-0.5" style={{ color: s.count }}>{regionCounts[r]}</div>}
                </button>
              )
            })}
          </div>
        )}

        {/* Filter toggles + sort pills */}
        {!isPaid ? (
          <div className="mb-3 flex items-center gap-2 text-sm text-gray-400">
            <span className="text-base">🔒</span>
            <span>Filter or sort —{' '}
              <a href="/pricing" className="text-blue-500 hover:underline font-medium">upgrade to Plus</a>
            </span>
          </div>
        ) : (
          <>
          <button
            onClick={() => setControlsOpen(v => !v)}
            className="mb-3 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className={`w-3.5 h-3.5 transition-transform ${controlsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            {controlsOpen ? 'Hide filters' : 'Filter & sort'}
          </button>
          {controlsOpen && <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-400">Filter by</span>
          {/* Has deals toggle */}
          <button
            onClick={() => setHasDealsOnly((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              hasDealsOnly
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'
            }`}
          >
            Has deals{' '}
            <span className={`ml-1 ${hasDealsOnly ? 'opacity-80' : 'opacity-60'}`}>
              ({totalWithDeals})
            </span>
          </button>

          {/* From my airports toggle */}
          <button
            onClick={() => currentUser && setFromMyAirports((v) => !v)}
            title={!currentUser ? 'Log in to filter by your home airports' : undefined}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              fromMyAirports && homeAirports.length
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'
            } ${!currentUser ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
          >
            {fromMyAirports && homeAirports.length
              ? `From ${homeAirports.map((ap) => getAirportCity(ap)).join(' & ')}`
              : 'From my airports'}
          </button>

          {/* Matches my dates toggle — only shown when user has travel windows */}
          {travelWindows.length > 0 && (
            <button
              onClick={() => setMatchMyDates((v) => !v)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                matchMyDates
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'
              }`}
            >
              📅 Matches my dates{' '}
              <span className={`ml-1 ${matchMyDates ? 'opacity-80' : 'opacity-60'}`}>
                ({matchingDestsCount})
              </span>
            </button>
          )}

          {/* Advanced filters toggle */}
          {(() => {
            const activeCount = [dateFrom || dateTo, maxPrice, selectedAirports.length > 0].filter(Boolean).length
            return (
              <button
                onClick={() => setFilterOpen((v) => !v)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-1 ${
                  filterOpen || activeCount > 0
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M7 12h10M11 20h2" />
                </svg>
                Filters
                {activeCount > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white text-blue-600 text-xs font-bold leading-none">
                    {activeCount}
                  </span>
                )}
              </button>
            )
          })()}

          {/* Visual divider */}
          <div className="hidden sm:block w-px h-5 bg-gray-200 mx-1" aria-hidden />

          {/* Sort by label + pills */}
          <span className="text-xs text-gray-400">Sort by</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSort(opt.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                sort === opt.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>}

        {controlsOpen && filterOpen && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
            {/* Date range */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Departure date</p>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="flex-1 min-w-0 text-sm rounded-lg border border-gray-200 bg-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <span className="text-gray-400 text-xs shrink-0">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="flex-1 min-w-0 text-sm rounded-lg border border-gray-200 bg-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Max price */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Max price</p>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">€</span>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  min="0"
                  placeholder="e.g. 200"
                  className="w-28 text-sm rounded-lg border border-gray-200 bg-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                {maxPrice && (
                  <button onClick={() => setMaxPrice('')} className="text-xs text-gray-400 hover:text-gray-600">
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Departure airport chips */}
            {availableAirports.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Departure airport</p>
                <div className="flex flex-wrap gap-2">
                  {availableAirports.map((ap) => (
                    <button
                      key={ap}
                      title={getAirportTooltip(ap)}
                      onClick={() =>
                        setSelectedAirports((prev) =>
                          prev.includes(ap) ? prev.filter((a) => a !== ap) : [...prev, ap]
                        )
                      }
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        selectedAirports.includes(ap)
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-400 hover:text-indigo-600'
                      }`}
                    >
                      {getAirportLabel(ap)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reset */}
            {(dateFrom || dateTo || maxPrice || selectedAirports.length > 0) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); setMaxPrice(''); setSelectedAirports([]) }}
                className="text-xs text-red-500 hover:text-red-700 hover:underline"
              >
                Reset all filters
              </button>
            )}
          </div>
        )}
          </>
        )}

        {/* Results count */}
        <p className="text-sm text-gray-500">
          Showing{' '}
          <span className="font-medium text-gray-700">{filtered.length}</span>{filtered.length !== groupedDestinations.length && <> of <span className="font-medium text-gray-700">{groupedDestinations.length}</span></>} destination{filtered.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* ── Grid ── */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">🔍</div>
          <div className="font-medium text-gray-600">No destinations found</div>
          <div className="text-sm mt-1">Try a different search or filter</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {filtered.map(({ primary: dest, airports }) => {
            const destDeals = airports.flatMap((d) => activeDealsByDest[d.code] ?? [])
            const hasDeals = destDeals.length > 0
                        const cheapestDeal = hasDeals ? destDeals.reduce((a, b) => b.price < a.price ? b : a) : null
            const cheapest = cheapestDeal?.price ?? null
            const maxDiscount = hasDeals ? Math.max(...destDeals.map((d) => d.discountPct ?? 0)) : 0
            const latestFetched = hasDeals
              ? destDeals.reduce((max, d) => (d.fetched_at ?? '') > max ? (d.fetched_at ?? '') : max, '')
              : null
            const bestMonth = cheapestMonths[dest.code] ?? null
            return (
              <Link
                key={dest.code}
                href={`/destinations/${dest.slug ?? dest.code.toLowerCase()}`}
                className={`group block rounded-xl border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all overflow-hidden${!hasDeals ? ' opacity-60' : ''}`}
              >
                {/* Image */}
                <div className="relative aspect-[4/3]">
                  {dest.image_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={dest.image_url}
                      alt={dest.city_name}
                      className={`absolute inset-0 w-full h-full object-cover${!hasDeals ? ' grayscale' : ''}`}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-600" />
                  )}
                  {/* Gradient overlay — desktop only */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent hidden sm:block" />

                  {/* Mobile: discount badge only */}
                  {hasDeals && maxDiscount >= 15 && (
                    <div className="sm:hidden absolute top-2 right-2">
                      <span className="bg-green-600/80 text-white text-xs font-semibold px-2 py-0.5 rounded-lg">
                        -{maxDiscount}%
                      </span>
                    </div>
                  )}

                  {/* Desktop: full badges top-left */}
                  {hasDeals ? (
                    <div className="hidden sm:flex absolute top-2 left-2 flex-col gap-1 items-start">
                      <span className="bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded-lg">
                        {destDeals.length} deal{destDeals.length !== 1 ? 's' : ''}
                      </span>
                      {(cheapest !== null || maxDiscount >= 15) && (
                        <div className="flex gap-1">
                          {cheapest !== null && (
                            <span className="bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded-lg">
                              from {formatDealPrice(cheapest).primary}
                            </span>
                          )}
                          {maxDiscount >= 15 && (
                            <span className="bg-green-600/80 text-white text-xs font-semibold px-2 py-0.5 rounded-lg">
                              up to -{maxDiscount}%
                            </span>
                          )}
                        </div>
                      )}
                      {hoursAgo(latestFetched) && (
                        <span className="bg-black/40 text-white/80 text-xs px-2 py-0.5 rounded-lg">
                          {hoursAgo(latestFetched)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="hidden sm:block absolute top-2 left-2">
                      <span className="bg-gray-600/80 text-white text-xs font-medium px-2 py-0.5 rounded-lg">
                        No current deals
                      </span>
                    </div>
                  )}

                  {/* Desktop: city info overlay bottom */}
                  <div className="hidden sm:block absolute bottom-0 left-0 p-2.5 text-white">
                    <div className="text-lg leading-none">{flagEmoji(dest.country_code)}</div>
                    <div className="mt-0.5 font-semibold text-sm leading-tight group-hover:text-blue-200 transition-colors">
                      {dest.city_name}
                    </div>
                    <div className="text-white/70 text-xs">{dest.country}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {airports.map((d) => (
                        <span key={d.code} className="inline-block bg-white/20 text-white text-xs px-1.5 py-0.5 rounded font-mono leading-none">
                          {d.code}
                        </span>
                      ))}
                    </div>
                    {bestMonth !== null && (
                      <div className="mt-0.5 text-white/70 text-xs font-medium">
                        📅 Best: {bestMonth.month} ~{formatDealPrice(bestMonth.avg).primary}
                      </div>
                    )}
                    {travelWindows.length > 0 && destDeals.some((d) => dealMatchesWindows(d, travelWindows)) && (
                      <div className="mt-1">
                        <span className="inline-block bg-emerald-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">
                          ✓ Matches your dates
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile: info panel below photo */}
                <div className="sm:hidden px-3 py-2.5 bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-base leading-none">{flagEmoji(dest.country_code)}</span>
                        <span className="font-semibold text-sm text-gray-900 leading-tight group-hover:text-blue-600 transition-colors truncate">
                          {dest.city_name}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{dest.country}</div>
                    </div>
                    {cheapest !== null && (
                      <span className="text-sm font-bold text-gray-900 shrink-0">
                        {formatDealPrice(cheapest).primary}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    {airports.map((d) => (
                      <span key={d.code} className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded font-mono leading-none">
                        {d.code}
                      </span>
                    ))}
                    {hasDeals && (
                      <span className="text-gray-400 text-xs">· {destDeals.length} deal{destDeals.length !== 1 ? 's' : ''}</span>
                    )}
                    {!hasDeals && <span className="text-gray-400 text-xs">No deals</span>}
                  </div>
                  {travelWindows.length > 0 && destDeals.some((d) => dealMatchesWindows(d, travelWindows)) && (
                    <div className="mt-1.5">
                      <span className="inline-block bg-emerald-50 text-emerald-700 text-xs font-semibold px-1.5 py-0.5 rounded">
                        ✓ Matches your dates
                      </span>
                    </div>
                  )}
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </>
  )
}
